/**
 * DREAM / REFLECTION SPOKEN PLAYBACK — one floor, owned above the components.
 *
 * WHY THIS EXISTS (Founder TestFlight, October 2026): two Vexis voices could speak at once inside
 * the Reflection experience, because the Reflection screen and the Dream conversation panel it
 * renders own separate players and neither knew about the other. A reflection and a dream reply are
 * the same voice; two of them at once is never something a member asked for.
 *
 * WHAT THIS IS NOT: this is NOT Message's session. `listenPlayback.js` is a regression-protected
 * surface and is deliberately not imported here, not extended and not joined to. Only the LIFECYCLE
 * PATTERN it proved is reused — ownership above the mount, one generation counter beside the owner,
 * and a returning mount reading the truth instead of trusting its own stale state. Message and Talk
 * never register here, so nothing in this file can stop them.
 *
 * WHO MAY REGISTER: Dream/Reflection-owned playback only (PremiumReflectionScreen, DreamConversation).
 *
 * WHY OWNERSHIP LIVES HERE: the Reflection element used to be a per-mount ref, which was only ever
 * correct while the screen stayed mounted. Playback does not stay mounted — the member walks to
 * another part of Arvexis while she is still speaking (Founder-approved, October 2026). So the
 * player, who holds the floor, what it is speaking and the counter that says "am I still the
 * current request?" all live at module scope, together.
 */

const IDLE = { owner: null, itemKey: null, state: 'idle', abort: null, stop: null };

let floor = { ...IDLE, generation: 0 };

const subscribers = new Set();

/** The snapshot a mount needs to show the truth, and nothing more. */
export const currentFloor = () => ({
  owner: floor.owner,
  itemKey: floor.itemKey,
  state: floor.state,
  playing: floor.state === 'loading' || floor.state === 'playing',
  generation: floor.generation
});

const notify = () => {
  const snapshot = currentFloor();
  subscribers.forEach((fn) => {
    try { fn(snapshot); } catch (e) { /* a subscriber's own render problem is not playback's */ }
  });
};

/** A mount subscribes on mount and unsubscribes on unmount. Unsubscribing never stops audio. */
export const subscribeFloor = (fn) => {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
};

export const floorGeneration = () => floor.generation;

/** The request that holds this number is the current one. Everything older discards itself. */
export const isCurrentFloor = (generation) => generation === floor.generation;

/**
 * Take the Dream/Reflection floor. Whatever held it stops HERE, not in the caller — so no surface
 * has to remember to stop every other surface. Returns the generation this request must carry.
 */
export const claimFloor = ({ owner, itemKey = null, stop = null, abort = null } = {}) => {
  const previousStop = floor.stop;
  const previousAbort = floor.abort;

  floor = { owner, itemKey, state: 'loading', abort, stop, generation: floor.generation + 1 };

  // Cancel the replaced request before silencing it: a response that is already in flight must not
  // arrive later and seize a floor it no longer holds.
  if (previousAbort) {
    try { previousAbort.abort(); } catch (e) { /* already settled */ }
  }
  if (previousStop) {
    try { previousStop({ replaced: true }); } catch (e) { /* already stopped */ }
  }

  notify();
  return floor.generation;
};

/** Only the holder may move the floor's state. A superseded request is ignored, not obeyed. */
export const setFloorState = (generation, state) => {
  if (!isCurrentFloor(generation)) return false;
  floor.state = state;
  notify();
  return true;
};

/** Natural end, manual stop, or a failure that leaves nothing speaking: the floor goes idle. */
export const releaseFloor = (generation) => {
  if (!isCurrentFloor(generation)) return false;
  floor = { ...IDLE, generation: floor.generation + 1 };
  notify();
  return true;
};

/**
 * Stop whatever Dream/Reflection playback is speaking. Works from a mount that did not start it,
 * because the stop closure lives here rather than in the component that happened to be on screen.
 */
export const stopCurrentFloor = () => {
  const generation = floor.generation;
  const stop = floor.stop;
  const abort = floor.abort;
  if (abort) {
    try { abort.abort(); } catch (e) { /* already settled */ }
  }
  if (stop) {
    try { stop({ replaced: false }); } catch (e) { /* already stopped */ }
  }
  // An owner that did not release the floor itself does not get to keep it.
  if (isCurrentFloor(generation)) releaseFloor(generation);
};

/**
 * THE REFLECTION PLAYER — one element, created inside the tap that first needs it (iOS only allows
 * programmatic playback on an element unlocked by a gesture) and kept at module scope afterwards so
 * leaving the screen does not destroy what she is saying.
 *
 * Dream keeps its own player and its own synthesis path; nothing here rewrites it.
 */
let reflectionPlayer = null;

export const reflectionAudio = () => {
  if (!reflectionPlayer) reflectionPlayer = new Audio();
  return reflectionPlayer;
};

export const hasReflectionAudio = () => Boolean(reflectionPlayer);
